// Client side of the world protocol. One ws connection; decodes server messages,
// keeps a cache of live agent state (for the Kazi card + tap registry), and
// re-emits everything onto the GlobalInfo bus so scenes can react. Buffers
// outgoing messages until the socket is open, and auto-reconnects.

import { PROTOCOL_VERSION, decode } from './protocol'
import type { AgentPublicState, Facing, LocationId, LogEntry, Tile, WorldFacts } from './protocol'
import { Direction } from 'grid-engine'
import GlobalInfo from '../GlobalInfo'

class ServerLink {
    enabled = false
    connected = false
    /** latest public state per agent id (across locations) — read by the Kazi card */
    readonly agents = new Map<string, AgentPublicState>()
    cost: { gameDayUsd: number; totalUsd: number; byModel: Record<string, number>; thinks: number; mode?: 'mock' | 'live' } | null = null
    clock: { dayName: string; hhmm: string; simEpochMs: number; nextSchoolOpenMs?: number | null } | null = null
    /** wall ms when `clock` was received — lets gameNow() tick between the 5s broadcasts */
    private clockAtWallMs = 0
    /** latest real-world snapshot (weather/calendar/football) — drives the weather widget */
    worldFacts: WorldFacts | null = null
    /** recent Journal log (seeded by log-history, appended by log-entry) */
    readonly log: LogEntry[] = []

    private ws?: WebSocket
    private url = ''
    private seq = 0
    private outbuf: string[] = []
    /** fired once: the server picked our spawn location (the busiest room) → switch scene to it */
    private spawned = false

    private _token: string | null = null

    connect(url?: string): void {
        this.enabled = true
        const host = (typeof location !== 'undefined' && location.hostname) || 'localhost'
        this.url = url || `ws://${host}:8787`
        ;(globalThis as any).LacazeNet = this
        this.loadToken()
        this.open()
    }

    // "Chez moi": pick up the companion-app session token from a deep-link (?token=…) or a prior session, so
    // the rooms directory can flag « Ma chambre » + attribute the player's reactions to their claimed avatar.
    // Same localStorage key as the companion ('lacaze_token') → shared automatically if ever served same-origin.
    private loadToken(): void {
        try {
            const ls = typeof localStorage !== 'undefined' ? localStorage : null
            const fromUrl = new URLSearchParams(typeof location !== 'undefined' ? location.search : '').get('token')
            this._token = fromUrl || ls?.getItem('lacaze_token') || null
            if (fromUrl && ls) ls.setItem('lacaze_token', fromUrl)
        } catch { /* no DOM (SSR / tests) */ }
    }

    /** Auth token if the player linked their companion session (else null → anonymous "Caz"). */
    get token(): string | null { return this._token }
    /** Authorization header for player-facing REST (directory « mine » flag, attributed reactions). */
    authHeaders(): Record<string, string> { return this._token ? { Authorization: `Bearer ${this._token}` } : {} }

    /** Current game-instant ms, extrapolated at 1:1 from the last clock broadcast (null before the first). */
    gameNow(): number | null {
        return this.clock ? this.clock.simEpochMs + (Date.now() - this.clockAtWallMs) : null
    }

    /** HTTP base of the world server (for fetching served assets like /avatars/<id>.png). */
    get httpBase(): string {
        if (this.url) return this.url.replace(/^ws/, 'http')
        const host = (typeof location !== 'undefined' && location.hostname) || 'localhost'
        return `http://${host}:8787`
    }
    /** Resolve a server-relative asset path to an absolute URL. */
    assetUrl(path: string): string {
        return /^(https?:|data:)/.test(path) ? path : this.httpBase + path
    }

    private open(): void {
        try { this.ws = new WebSocket(this.url) } catch { this.scheduleReconnect(); return }
        // The human is always Caz, l'élève fantôme — identity is pinned server-side, so we send no
        // displayName (the old hardcoded 'Maxence' made every visitor greeted by the intern's name).
        this.ws.onopen = () => { this.connected = true; this.rawSend('hello', {}); this.flush() }
        this.ws.onmessage = (ev) => this.onMessage(String(ev.data))
        this.ws.onclose = () => { this.connected = false; this.scheduleReconnect() }
        this.ws.onerror = () => { /* close will follow */ }
    }

    private scheduleReconnect(): void {
        if (!this.enabled) return
        setTimeout(() => this.open(), 1500)
    }

    private flush(): void {
        for (const m of this.outbuf) this.ws!.send(m)
        this.outbuf = []
    }

    private rawSend(t: string, p: unknown): void {
        const msg = JSON.stringify({ v: PROTOCOL_VERSION, t, seq: ++this.seq, ts: Date.now(), p })
        if (this.connected && this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(msg)
        else this.outbuf.push(msg)
    }

    // ── public API ───────────────────────────────────────────────────────────
    enterLocation(location: LocationId): void { this.rawSend('enter-location', { location }) }
    playerMove(location: LocationId, tile: Tile, facing?: Facing): void { this.rawSend('player-move', { location, tile, facing }) }
    interact(targetAgentId: string): void { this.rawSend('interact', { targetAgentId }) }
    playerSay(targetAgentId: string, text: string, quick?: boolean): void { this.rawSend('player-say', { targetAgentId, text, quick }) }
    queryState(agentId: string): void { this.rawSend('query-state', { agentId }) }
    queryConversation(agentId: string): void { this.rawSend('query-conversation', { agentId }) }
    setIntent(agentId: string, intent: string): void { this.rawSend('set-intent', { agentId, intent }) }
    devTime(multiplier: number): void { this.rawSend('dev-time', { multiplier }) }
    getAgent(id: string): AgentPublicState | undefined { return this.agents.get(id) }

    private onMessage(raw: string): void {
        const m = decode(raw) as any
        if (!m || m.v !== PROTOCOL_VERSION) return
        switch (m.t) {
            case 'snapshot':
                for (const a of m.p.agents as AgentPublicState[]) this.agents.set(a.id, a)
                GlobalInfo.emit('server-snapshot', m.p)
                // The first snapshot carrying `you` is the spawn snapshot: the server placed Caz in the
                // busiest location. The client always boots Campus, so signal a one-time scene switch.
                if (!this.spawned && m.p.you) { this.spawned = true; GlobalInfo.emit('server-spawn', { location: m.p.location }) }
                break
            case 'agent-move': GlobalInfo.emit('server-agent-move', m.p); break
            case 'agent-teleport': GlobalInfo.emit('server-agent-teleport', m.p); break
            case 'agent-action': GlobalInfo.emit('server-agent-action', m.p); break
            case 'agent-state':
                this.agents.set(m.p.agent.id, m.p.agent)
                GlobalInfo.emit('server-agent-state', m.p)
                break
            case 'event-beat': GlobalInfo.emit('server-event-beat', m.p); break
            case 'cost-meter': this.cost = m.p; GlobalInfo.emit('server-cost-meter', m.p); break
            case 'clock': this.clock = m.p; this.clockAtWallMs = Date.now(); GlobalInfo.emit('server-clock', m.p); break
            case 'world-pulse': GlobalInfo.emit('server-world-pulse', m.p); break
            case 'world-facts': this.worldFacts = m.p.facts; GlobalInfo.emit('server-world-facts', m.p); break
            case 'recap-ready': GlobalInfo.emit('server-recap-ready', m.p); break
            case 'world-object-spawn': GlobalInfo.emit('server-world-object-spawn', m.p); break
            case 'world-object-remove': GlobalInfo.emit('server-world-object-remove', m.p); break
            case 'conversation-history': GlobalInfo.emit('server-conversation-history', m.p); break
            case 'log-history':
                this.log.length = 0
                for (const e of m.p.entries as LogEntry[]) this.log.push(e)
                GlobalInfo.emit('server-log-history', m.p)
                break
            case 'log-entry':
                this.log.push(m.p.entry)
                if (this.log.length > 400) this.log.splice(0, this.log.length - 400)
                GlobalInfo.emit('server-log-entry', m.p)
                break
        }
    }
}

export function facingToDir(f: Facing | undefined): Direction {
    switch (f) {
        case 'up': return Direction.UP
        case 'left': return Direction.LEFT
        case 'right': return Direction.RIGHT
        default: return Direction.DOWN
    }
}

export function dirToFacing(d: Direction): Facing {
    switch (d) {
        case Direction.UP: return 'up'
        case Direction.LEFT: return 'left'
        case Direction.RIGHT: return 'right'
        default: return 'down'
    }
}

export default new ServerLink()
